import React, { useEffect, useState } from 'react';

// Vertical wind trails — centered around where the arrow was, fanning out slightly
const WIND_LINES = [
    // [leftOffset%, width, height, delay, duration]
    [ 0,   3, 260,   0, 0.5],
    [-12,  2, 200,  30, 0.5],
    [ 12,  2, 200,  30, 0.5],
    [-24,  2, 160,  60, 0.52],
    [ 24,  2, 160,  60, 0.52],
    [-36,  2, 120,  90, 0.54],
    [ 36,  2, 120,  90, 0.54],
    [-48,  1,  90, 120, 0.56],
    [ 48,  1,  90, 120, 0.56],
    [-60,  1,  65, 160, 0.58],
    [ 60,  1,  65, 160, 0.58],
    [-72,  1,  45, 200, 0.60],
    [ 72,  1,  45, 200, 0.60],
    // second wave — slightly behind
    [ -6,  2, 220,  50, 0.48],
    [  6,  2, 220,  50, 0.48],
    [-18,  1, 170,  80, 0.52],
    [ 18,  1, 170,  80, 0.52],
    [-30,  1, 130, 110, 0.54],
    [ 30,  1, 130, 110, 0.54],
    [-42,  1,  80, 150, 0.56],
    [ 42,  1,  80, 150, 0.56],
];

export default function Intro({ onComplete }) {
    const [phase, setPhase] = useState('idle');
    const [windKey, setWindKey] = useState(0);

    useEffect(() => {
        const t1 = setTimeout(() => setPhase('zoom'),  100);
        const t2 = setTimeout(() => setPhase('trail'), 900);
        const t3 = setTimeout(() => setPhase('text'),  1700);
        const t4 = setTimeout(() => { setPhase('flyUp'); setWindKey(k => k + 1); }, 2800);
        const t5 = setTimeout(() => setPhase('exit'),  3700);
        const t6 = setTimeout(() => onComplete(),      3900);
        return () => [t1, t2, t3, t4, t5, t6].forEach(clearTimeout);
    }, [onComplete]);

    const showWind = phase === 'flyUp' || phase === 'exit';

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: '#0a4abe',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden',
            opacity: phase === 'exit' ? 0 : 1,
            transition: phase === 'exit' ? 'opacity 0.2s ease' : 'none',
            pointerEvents: phase === 'exit' ? 'none' : 'all',
        }}>

            {/* Background grid */}
            <div style={{
                position: 'absolute', inset: 0,
                backgroundImage: 'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
                backgroundSize: '48px 48px',
                opacity: phase === 'trail' || phase === 'text' ? 1 : 0,
                transition: 'opacity 0.6s ease',
            }} />

            {/* Radial speed lines on trail */}
            {phase === 'trail' && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {[...Array(12)].map((_, i) => (
                        <div key={i} style={{
                            position: 'absolute', width: '2px', height: 0,
                            background: 'linear-gradient(to top, rgba(249,216,0,0.6), transparent)',
                            transformOrigin: 'bottom center',
                            transform: `rotate(${i * 30}deg) translateY(-60px)`,
                            animation: 'speedLine 0.5s ease-out forwards',
                            animationDelay: `${i * 20}ms`,
                            borderRadius: '2px',
                        }} />
                    ))}
                </div>
            )}

            {/* Ripple rings */}
            {(phase === 'zoom' || phase === 'trail' || phase === 'text') && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                    {[0, 1, 2].map(i => (
                        <div key={i} style={{
                            position: 'absolute', width: '80px', height: '80px',
                            borderRadius: '50%', border: '2px solid rgba(249,216,0,0.4)',
                            animation: 'ripple 1.4s ease-out infinite',
                            animationDelay: `${i * 0.45}s`,
                        }} />
                    ))}
                </div>
            )}

            {/* Vertical wind trails — left in arrow's wake */}
            {showWind && (
                <div key={windKey} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {WIND_LINES.map(([offsetX, w, h, delay, dur], i) => (
                        <div key={i} style={{
                            position: 'absolute',
                            top: '50%',
                            left: `calc(50% + ${offsetX}px)`,
                            width: `${w}px`,
                            height: `${h}px`,
                            borderRadius: '999px',
                            // Gradient: bright at top (where arrow just was) fading down
                            background: 'linear-gradient(to bottom, rgba(255,255,255,0.95) 0%, rgba(200,220,255,0.6) 40%, rgba(150,190,255,0.2) 75%, transparent 100%)',
                            opacity: 0,
                            transformOrigin: 'top center',
                            animation: `windTrail ${dur}s ease-out forwards`,
                            animationDelay: `${delay}ms`,
                        }} />
                    ))}
                </div>
            )}

            {/* Flash burst at launch */}
            {phase === 'flyUp' && (
                <div style={{
                    position: 'absolute', zIndex: 3,
                    width: '140px', height: '140px', borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(249,216,0,0.95) 0%, rgba(249,216,0,0.4) 40%, transparent 70%)',
                    animation: 'flash 0.3s ease-out forwards',
                    pointerEvents: 'none',
                }} />
            )}

            {/* Arrow + brand */}
            <div style={{ position: 'relative', zIndex: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '28px' }}>
                <div style={{
                    position: 'relative', width: '160px', height: '160px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    ...(phase === 'idle'  && { opacity: 0, transform: 'scale(0.3) rotate(-15deg)', transition: 'none' }),
                    ...(phase === 'zoom'  && { opacity: 1, transform: 'scale(1.15) rotate(0deg)', transition: 'opacity 0.5s ease-out, transform 0.6s cubic-bezier(0.34,1.56,0.64,1)' }),
                    ...(phase === 'trail' && { opacity: 1, transform: 'scale(0.85) translateY(-8px) rotate(-8deg)', transition: 'transform 0.5s cubic-bezier(0.4,0,0.2,1)' }),
                    ...(phase === 'text'  && { opacity: 1, transform: 'scale(1) translateY(-4px) rotate(0deg)', transition: 'transform 0.6s cubic-bezier(0.34,1.2,0.64,1)' }),
                    ...(phase === 'flyUp' && { opacity: 0, transform: 'scale(0.4) translateY(-900px) rotate(-5deg)', transition: 'transform 0.28s cubic-bezier(0.55,0,1,0.45), opacity 0.15s ease 0.15s' }),
                    ...(phase === 'exit'  && { opacity: 0, transform: 'scale(0.3) translateY(-1200px)', transition: 'none' }),
                }}>
                    <div style={{
                        position: 'absolute', inset: '-20px', borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(249,216,0,0.25) 0%, transparent 70%)',
                        opacity: phase === 'zoom' || phase === 'trail' || phase === 'text' ? 1 : 0,
                        transition: 'opacity 0.4s ease',
                    }} />
                    <img src="/assets/arrow.png" alt="" style={{
                        width: '140px', height: '140px', objectFit: 'contain',
                        filter: 'drop-shadow(0 0 24px rgba(249,216,0,0.5))',
                        position: 'relative', zIndex: 1,
                    }} />
                </div>

                <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                    opacity: phase === 'text' ? 1 : 0,
                    transform: phase === 'text' ? 'translateY(0)' : (phase === 'flyUp' || phase === 'exit') ? 'translateY(12px)' : 'translateY(16px)',
                    transition: 'opacity 0.5s ease, transform 0.5s cubic-bezier(0.34,1.2,0.64,1)',
                }}>
                    <p style={{ fontFamily: "'Bebas Neue', sans-serif", fontWeight: 800, fontSize: '36px', color: '#fff', letterSpacing: '1px', lineHeight: 1, margin: 0 }}>
                        Delivery <span style={{ color: '#f9d800' }}>Hub</span>
                    </p>
                    <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300, fontSize: '13px', color: 'rgba(255,255,255,0.55)', letterSpacing: '3px', textTransform: 'uppercase', margin: 0 }}>
                        Lebanon's Delivery Network
                    </p>
                </div>
            </div>

            {/* Progress bar */}
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '3px', background: 'rgba(255,255,255,0.1)', zIndex: 5 }}>
                <div style={{
                    height: '100%', background: '#f9d800',
                    width: phase === 'idle' ? '0%' : phase === 'zoom' ? '35%' : phase === 'trail' ? '65%' : phase === 'text' ? '85%' : phase === 'flyUp' ? '95%' : '100%',
                    transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)',
                    boxShadow: '0 0 10px rgba(249,216,0,0.6)',
                }} />
            </div>

            <style>{`
        @keyframes ripple    { 0% { transform: scale(1); opacity: 0.7; } 100% { transform: scale(6); opacity: 0; } }
        @keyframes speedLine { 0% { height: 0; opacity: 0; } 30% { opacity: 1; } 100% { height: 120px; opacity: 0; } }
        @keyframes flash     { 0% { transform: scale(0.2); opacity: 1; } 100% { transform: scale(3); opacity: 0; } }
        @keyframes windTrail {
          0%   { opacity: 0;    transform: scaleY(0) translateY(-20px); }
          15%  { opacity: 1;    transform: scaleY(1) translateY(0px); }
          65%  { opacity: 0.8;  transform: scaleY(1) translateY(18px); }
          100% { opacity: 0;    transform: scaleY(1.1) translateY(40px); }
        }
      `}</style>
        </div>
    );
}